import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  addIndex,
  filter,
  includes,
  map,
  not,
  path,
  propEq,
  reject,
} from "ramda";
import { useDispatch, useSelector } from "react-redux";
import {
  AUDIENCE_CHANGED,
  SUB_PROJECT_CHANGED,
  COMPLETE_STEP,
  FIELD_BLURRED,
  FIELD_CHANGED,
  SHOW_ADD_CONTRIBUTOR_DIALOG,
  SHOW_EDIT_CONTRIBUTOR_DIALOG,
  SHOW_REMOVE_CONTRIBUTOR_DIALOG,
  LICENSE_TYPE_CHANGED,
  VIDEO_RATING_CHANGED,
  CUSTOM_DROPDOWN_CHANGED,
} from "file-details/fileDetailsActions";
import {
  SET_ACTIVE_STEP,
  SHOW_CANCEL_SUBMISSION_DIALOG,
} from "submission/submissionActions";
import {
  getAgeRanges,
  getAudiences,
  getCollectPurchaseLink,
  getContributors,
  getLicenseTypes,
  getStepErrors,
  getStepFields,
  getVideoRatings,
  isDetailsStepLoading,
  getSubProjects,
  getFieldOptions,
  getWorkType,
  getCustomDropdownItems,
} from "common/selectors";
import { formatContributorName } from "common/stringFormat";
import { firstError, hasError } from "common/validation";
import { isBlank, notBlank } from "common/general";
import { AGE_RANGE_CHANGED } from "../fileDetailsActions";
import { useTranslation } from "react-i18next";
import * as steps from "common/steps";
import clsx from "clsx";
import grey from "@material-ui/core/colors/grey";
import { useIsQatarProject } from "../../common/projectUtils";

import LoadingPanel from "general-components/LoadingPanel";
import StepPanelButtonBar from "general-components/StepPanelButtonBar";
import AddContributorDialog from "./AddContributorDialog";
import AlbumDetailsFields from "./AlbumDetailsFields";
import AudioDetailsFields from "./AudioDetailsFields";
import BookDetailsFields from "./BookDetailsFields";
import DocumentDetailsFields from "./DocumentDetailsFields";
import EditContributorDialog from "./EditContributorDialog";
import ExternalLinks from "./ExternalLinks";
import ImageDetailsFields from "./ImageDetailsFields";
import RemoveContributorDialog from "./RemoveContributorDialog";
import VideoDetailsFields from "./VideoDetailsFields";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import Typography from "@material-ui/core/Typography";

import IntlAutocomplete from "../../general-components/IntlAutocomplete";
import IntlTextField from "../../general-components/IntlTextField";
import CustomizableTextField from "../../general-components/CustomizableTextField";
import HidableGridItem from "../../general-components/HidableGridItem";
import AutocompleteInputField from "../../general-components/AutocompleteInputField";
import EditAlbumTracks from "./EditAlbumTracks";
import ArticleDetailsFields from "./ArticleDetailsFields";
import { getFieldOptionLabel } from "../../common/stringFormat";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 2,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  sectionHeader: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  addContributorButton: {
    textTransform: "uppercase",
    fontWeight: "bolder",
    marginTop: theme.spacing(1),
  },

  contributorsContainer: {
    maxWidth: 600,
    borderBottomStyle: "dashed",
    borderBottomColor: grey[300],
  },

  contributorsContainerError: {
    maxWidth: 600,
    borderBottomStyle: "dashed",
    borderBottomColor: theme.palette.primary.error,
  },

  contributorsError: {
    color: theme.palette.primary.error,
    marginBottom: theme.spacing(1),
  },

  contributorRow: {
    display: "flex",
    alignItems: "center",
    // marginBottom: theme.spacing(2),
  },

  contributorName: {
    fontWeight: "bold",
    fontSize: "1.13rem",
  },
  startIcon: {
    marginLeft: 8,
    marginRight: "-4px",
  },
}));

const withoutOhioLinkLicense = reject(propEq("code", "OHIO_LINK_CP"));

const isNotBookOrAudiobook = workType =>
  not(includes(workType.contentTypeName, ["BOOK", "AUDIOBOOK"]));

const FileDetails = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isQatarProject = useIsQatarProject();

  const subProjects = useSelector(getSubProjects);
  const customDropdownItems = useSelector(getCustomDropdownItems);
  const fieldOptions = useSelector(getFieldOptions);
  const audiences = useSelector(getAudiences);
  const collectPurchaseLink = useSelector(getCollectPurchaseLink);
  const contributors = useSelector(getContributors);
  const errors = useSelector(getStepErrors(steps.FILE_DETAILS));
  const fields = useSelector(getStepFields(steps.FILE_DETAILS));
  const licenseTypes = useSelector(getLicenseTypes);
  const ageRanges = useSelector(getAgeRanges);
  const loading = useSelector(isDetailsStepLoading);
  const videoRatings = useSelector(getVideoRatings);
  const workType = useSelector(getWorkType);

  const [showGeneralInfoHeader, setShowGeneralInfoHeader] = useState(true);
  const setGeneralInfoRef = useCallback(ref => {
    const observer = new MutationObserver(mutationsList => {
      for (let mutation of mutationsList) {
        if (mutation.type === "childList") {
          if (ref.children.length === 0) {
            setShowGeneralInfoHeader(false);
          }
        }
      }
    });

    if (notBlank(ref)) {
      observer.observe(ref, { childList: true });
    }
  }, []);

  const showContributors =
    (fieldOptions?.contributors && !fieldOptions.contributors?.hidden) ?? true;

  const showExternalLinks =
    notBlank(fieldOptions.external_links) &&
    !fieldOptions.external_links.hidden &&
    includes(workType.contentTypeName, ["BOOK", "AUDIOBOOK"]);

  const [activeSubProjects, setActiveSubProjects] = useState([]);
  useEffect(() => {
    if (notBlank(subProjects)) {
      setActiveSubProjects(filter(sb => sb.active, subProjects));
    }
  }, [subProjects]);

  const [activeCustomDropdownItems, setCustomDropdownItems] = useState([]);
  useEffect(() => {
    if (notBlank(customDropdownItems)) {
      setCustomDropdownItems(filter(cdi => cdi.active, customDropdownItems));
    }
  }, [customDropdownItems]);

  const dispatch = useDispatch();
  const onAddContributor = () =>
    dispatch({ type: SHOW_ADD_CONTRIBUTOR_DIALOG });
  const onBlur = (field, value) => () =>
    dispatch({ type: FIELD_BLURRED, field, value });
  const onEditContributor = index => () =>
    dispatch({ type: SHOW_EDIT_CONTRIBUTOR_DIALOG, index });
  const onNext = () => dispatch({ type: COMPLETE_STEP });
  const onPrevious = () =>
    dispatch({ type: SET_ACTIVE_STEP, step: steps.FILE_UPLOAD });
  const onCancel = () =>
    dispatch({ type: SHOW_CANCEL_SUBMISSION_DIALOG, show: true });
  const onRemoveContributor = index => () =>
    dispatch({ type: SHOW_REMOVE_CONTRIBUTOR_DIALOG, index });
  const onAudienceChange = (event, audience) =>
    dispatch({ type: AUDIENCE_CHANGED, audience });
  const onSubProjectChange = (event, subProject) =>
    dispatch({ type: SUB_PROJECT_CHANGED, subProject });

  const onCustomDropdownChange = (event, customDropdownItem) =>
    dispatch({ type: CUSTOM_DROPDOWN_CHANGED, customDropdownItem });

  const onVideoRatingChange = (event, rating) =>
    dispatch({ type: VIDEO_RATING_CHANGED, rating });
  const onLicenseTypeChange = (event, licenseType) =>
    dispatch({ type: LICENSE_TYPE_CHANGED, licenseType });
  const onAgeRangeChange = (event, ageRange) =>
    dispatch({ type: AGE_RANGE_CHANGED, ageRange });
  const onFieldChange = path => event => {
    const value = notBlank(event.target) ? event.target.value : event;
    dispatch({ type: FIELD_CHANGED, path, value });
  };

  const createContributorRow =
    (onEditContributor, onRemoveContributor, isRtl) => (contributor, index) => {
      return (
        <div
          key={`details-contributor-${index}`}
          className={classes.contributorRow}
        >
          <span className={classes.contributorName}>
            {formatContributorName(contributor, isRtl)}
          </span>
          <IconButton
            aria-label={`delete contributor ${formatContributorName(
              contributor,
            )}`}
            onClick={onRemoveContributor(index)}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            aria-label={`edit contributor ${formatContributorName(
              contributor,
            )}`}
            onClick={onEditContributor(index)}
          >
            <EditIcon />
          </IconButton>
        </div>
      );
    };

  return loading ? (
    <LoadingPanel />
  ) : (
    <Paper classes={{ root: classes.root }} elevation={0}>
      <form autoComplete="none">
        <div className={classes.sectionHeader}>
          <Typography variant="h2">
            {t("file_details_about_work")}{" "}
            <Typography variant="body2">
              {t("file_details_about_work_sub")}
            </Typography>
          </Typography>
        </div>

        {includes(workType.contentTypeName, ["BOOK", "AUDIOBOOK"]) && (
          <BookDetailsFields
            errors={errors}
            fields={fields}
            onBlur={onBlur}
            onFieldChange={onFieldChange}
          />
        )}

        {workType.contentTypeName === "ALBUM" && (
          <AlbumDetailsFields
            errors={errors}
            fields={fields}
            onBlur={onBlur}
            onFieldChange={onFieldChange}
          />
        )}

        {workType.contentTypeName === "ARTICLE" && (
          <ArticleDetailsFields
            errors={errors}
            fields={fields}
            onBlur={onBlur}
            onFieldChange={onFieldChange}
          />
        )}

        {workType.contentTypeName === "AUDIO" && (
          <AudioDetailsFields
            errors={errors}
            fields={fields}
            onBlur={onBlur}
            onFieldChange={onFieldChange}
          />
        )}
        {workType.contentTypeName === "IMAGE" && (
          <ImageDetailsFields
            errors={errors}
            fields={fields}
            onBlur={onBlur}
            onFieldChange={onFieldChange}
          />
        )}
        {workType.contentTypeName === "OTHER_DOCUMENT" && (
          <DocumentDetailsFields
            errors={errors}
            fields={fields}
            onBlur={onBlur}
            onFieldChange={onFieldChange}
          />
        )}
        {workType.contentTypeName === "VIDEO" && (
          <VideoDetailsFields
            videoRatings={videoRatings}
            errors={errors}
            fields={fields}
            onBlur={onBlur}
            onFieldChange={onFieldChange}
            onVideoRatingChange={onVideoRatingChange}
          />
        )}

        {workType.contentTypeName === "ALBUM" && (
          <Fragment>
            <div className={classes.sectionHeader}>
              <Typography variant="h2">
                {t("file_details_track_info", "Track information")}{" "}
                <Typography variant="body2">
                  {t(
                    "file_details_track_info_sub",
                    "Edit individual track information.",
                  )}
                </Typography>
              </Typography>
            </div>

            <EditAlbumTracks />
          </Fragment>
        )}

        <div className={classes.sectionHeader}>
          {showGeneralInfoHeader && (
            <Typography variant="h2">
              {t("file_details_general_information")}{" "}
              <Typography variant="body2">
                {t("file_details_general_information_sub")}
              </Typography>
            </Typography>
          )}
        </div>

        <Grid container spacing={2} ref={setGeneralInfoRef}>
          {!!activeSubProjects.length && !fieldOptions["sub_projects"].hidden && (
            <Grid item xs={12} sm={6}>
              <IntlAutocomplete
                id="subproject"
                disableClearable
                autoSelect
                options={activeSubProjects}
                value={path(["subProject"], fields) || ""}
                getOptionLabel={opt =>
                  notBlank(opt) ? opt.subProjectName : ""
                }
                getOptionSelected={(opt, val) =>
                  isBlank(val) ||
                  opt.subProjectId === val.subProjectId ||
                  opt.subProjectId === val
                }
                onChange={onSubProjectChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = "subproject";
                  return (
                    <IntlTextField
                      {...params}
                      type="text"
                      onBlur={onBlur(
                        "subProject",
                        path(["subProject"], fields),
                      )}
                      error={hasError(errors, "subProject")}
                      helperText={t(firstError(errors, "subProject"), {
                        subProjectLabel:
                          fieldOptions["sub_projects"].fieldLabel,
                      })}
                      name={"subProject-" + Math.random()}
                      variant="standard"
                      label={
                        fieldOptions["sub_projects"].fieldLabel ||
                        t("file_details.sub_project", "Sub Project")
                      }
                      fullWidth
                    />
                  );
                }}
              />
            </Grid>
          )}

          {!!activeCustomDropdownItems.length &&
            !fieldOptions["custom_dropdown"].hidden && (
              <Grid item xs={12} sm={6}>
                <IntlAutocomplete
                  id={getFieldOptionLabel("custom_dropdown", fieldOptions)}
                  disableClearable
                  autoSelect
                  options={activeCustomDropdownItems}
                  value={path(["customDropdown"], fields) || ""}
                  getOptionLabel={opt =>
                    notBlank(opt) ? opt.customDropdownName : ""
                  }
                  getOptionSelected={(opt, val) =>
                    isBlank(val) ||
                    opt.customDropdownId === val.customDropdownId ||
                    opt.customDropdownId === val
                  }
                  onChange={onCustomDropdownChange}
                  renderInput={params => {
                    params.inputProps["aria-autocomplete"] = "none";
                    params.inputProps["id"] = getFieldOptionLabel(
                      "custom_dropdown",
                      fieldOptions,
                    );
                    return (
                      <IntlTextField
                        {...params}
                        type="text"
                        onBlur={onBlur(
                          "customDropdown",
                          path(["customDropdown"], fields),
                        )}
                        error={hasError(errors, "customDropdown")}
                        helperText={t(firstError(errors, "customDropdown"), {
                          customDropdownLabel:
                            fieldOptions["custom_dropdown"].fieldLabel,
                        })}
                        name={"customDropdown-" + Math.random()}
                        variant="standard"
                        label={
                          fieldOptions["custom_dropdown"].fieldLabel ||
                          t("file_details.custom_dropdown", "Custom Dropdown")
                        }
                        fullWidth
                      />
                    );
                  }}
                />
              </Grid>
            )}

          {/*Only show if there is a label for the field and it's not marked as hidden*/}
          {fieldOptions["custom_freeform"] &&
            fieldOptions["custom_freeform"].fieldLabel &&
            !fieldOptions["custom_freeform"].hidden && (
              <Grid item xs={12} sm={6}>
                <IntlTextField
                  id={getFieldOptionLabel("custom_freeform", fieldOptions)}
                  type="text"
                  label={
                    fieldOptions["custom_freeform"].fieldLabel ||
                    t("file_details.custom_freeform", "Custom Freeform")
                  }
                  value={path(["customFreeform"], fields) || ""}
                  onChange={onFieldChange(["customFreeform"])}
                  error={hasError(errors, "customFreeform")}
                  helperText={t(firstError(errors, "customFreeform"), {
                    customFreeformLabel:
                      fieldOptions["custom_freeform"].fieldLabel,
                  })}
                  onBlur={onBlur("customFreeform", fields.customFreeform)}
                  inputProps={{
                    maxLength: 1028,
                    id: getFieldOptionLabel("custom_freeform", fieldOptions),
                  }}
                />
              </Grid>
            )}

          {fieldOptions["custom_freeform_2"] &&
            fieldOptions["custom_freeform_2"].fieldLabel &&
            !fieldOptions["custom_freeform_2"].hidden && (
              <Grid item xs={12} sm={6}>
                <IntlTextField
                  id={getFieldOptionLabel("custom_freeform_2", fieldOptions)}
                  type="text"
                  label={
                    fieldOptions["custom_freeform_2"].fieldLabel ||
                    t("file_details.custom_freeform_2", "Custom Freeform")
                  }
                  value={path(["customFreeform2"], fields) || ""}
                  onChange={onFieldChange(["customFreeform2"])}
                  error={hasError(errors, "customFreeform2")}
                  helperText={t(firstError(errors, "customFreeform2"), {
                    customFreeform2Label:
                      fieldOptions["custom_freeform_2"].fieldLabel,
                  })}
                  onBlur={onBlur("customFreeform2", fields.customFreeform2)}
                  inputProps={{
                    maxLength: 1028,
                    id: getFieldOptionLabel("custom_freeform_2", fieldOptions),
                  }}
                />
              </Grid>
            )}

          {notBlank(audiences) && (
            <HidableGridItem fieldOption="audience" xs={12} sm={6}>
              <IntlAutocomplete
                id="audience"
                disableClearable
                autoSelect
                options={audiences}
                value={path(["audience"], fields) || ""}
                getOptionLabel={audience =>
                  notBlank(audience) ? audience.display : ""
                }
                getOptionSelected={(opt, val) =>
                  isBlank(val) || opt.name === val.name
                }
                onChange={onAudienceChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = "audience";
                  return (
                    <AutocompleteInputField
                      params={params}
                      fieldOption="audience"
                      onBlur={onBlur("audience", path(["audience"], fields))}
                      error={hasError(errors, "audience")}
                      helperText={t(firstError(errors, "audience"))}
                      name={"audience-" + Math.random()}
                      defaultLabel={t(
                        "file_details.audience",
                        "Who is the audience for your work?",
                      )}
                    />
                  );
                }}
              />
            </HidableGridItem>
          )}

          {notBlank(ageRanges) && fieldOptions["age-range"] && (
            <HidableGridItem fieldOption="age_range" xs={12} sm={6}>
              <IntlAutocomplete
                id="age-range"
                options={ageRanges}
                value={path(["ageRange"], fields) || ""}
                getOptionLabel={ageRange =>
                  notBlank(ageRange) ? ageRange.display : ""
                }
                getOptionSelected={(opt, val) =>
                  isBlank(val) || opt.name === val.name
                }
                onChange={onAgeRangeChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = "age-range";
                  return (
                    <AutocompleteInputField
                      params={params}
                      fieldOption="ageRange"
                      onBlur={onBlur("ageRange", path(["ageRange"], fields))}
                      error={hasError(errors, "ageRange")}
                      helperText={t(firstError(errors, "ageRange"))}
                      name={"ageRange-" + Math.random()}
                      defaultLabel={t("file_details.age_range", "Age Range")}
                    />
                  );
                }}
              />
            </HidableGridItem>
          )}

          {notBlank(licenseTypes) && fieldOptions["license_type"] && (
            <HidableGridItem fieldOption="license_type" xs={12} sm={6}>
              <IntlAutocomplete
                id="license-type"
                options={withoutOhioLinkLicense(licenseTypes)}
                value={path(["licenseType"], fields) || ""}
                getOptionLabel={licenseType =>
                  notBlank(licenseType) ? licenseType.name : ""
                }
                getOptionSelected={(opt, val) =>
                  isBlank(val) || opt.name === val.name
                }
                onChange={onLicenseTypeChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = "license-type";
                  return (
                    <AutocompleteInputField
                      params={params}
                      fieldOption="licenseType"
                      onBlur={onBlur(
                        "licenseType",
                        path(["licenseType"], fields),
                      )}
                      error={hasError(errors, "licenseType")}
                      helperText={t(firstError(errors, "licenseType"))}
                      name={"licenseType-" + Math.random()}
                      defaultLabel={t(
                        "file_details.license_type",
                        "License Type",
                      )}
                    />
                  );
                }}
              />
            </HidableGridItem>
          )}

          {collectPurchaseLink && (
            <CustomizableTextField
              id="purchase-url"
              xs={12}
              sm={6}
              name="purchaseUrl"
              fieldOption="purchase_url"
              defaultLabel={t(
                "file_details.purchase_url",
                "Purchase URL if available in print (optional)",
              )}
              value={path(["purchaseUrl"], fields) || ""}
              onChange={onFieldChange(["purchaseUrl"])}
              error={hasError(errors, "purchaseUrl")}
              helperText={t(firstError(errors, "purchaseUrl"))}
              onBlur={onBlur("purchaseUrl", fields.purchaseUrl)}
              inputProps={{ maxLength: 2048, id: "purchase-url" }}
            />
          )}

          <CustomizableTextField
            id="publisher"
            xs={12}
            sm={6}
            name="publisher"
            fieldOption="publisher_name"
            defaultLabel={t(
              "file_details.publisher",
              "Publisher name (optional)",
            )}
            hideByDefault={isNotBookOrAudiobook(workType)}
            value={path(["publisher"], fields) || ""}
            onChange={onFieldChange(["publisher"])}
            error={hasError(errors, "publisher")}
            helperText={t(firstError(errors, "publisher"))}
            onBlur={onBlur("publisher", fields.publisher)}
            inputProps={{ maxLength: 255, id: "publisher" }}
          />

          <CustomizableTextField
            id="publish-on-demand-vendor"
            xs={12}
            sm={6}
            name="vendor"
            fieldOption="pod_vendor"
            defaultLabel={t(
              "file_details.pod_vendor",
              "Publish on Demand (POD) vendor (optional)",
            )}
            hideByDefault={isNotBookOrAudiobook(workType)}
            value={path(["vendor"], fields) || ""}
            onChange={onFieldChange(["vendor"])}
            error={hasError(errors, "vendor")}
            helperText={t(firstError(errors, "vendor"))}
            onBlur={onBlur("vendor", fields.vendor)}
            inputProps={{ maxLength: 255, id: "publish-on-demand-vendor" }}
          />
        </Grid>

        <Grid container item xs spacing={2}>
          {showContributors && (
            <Grid item xs={12} md={6}>
              <div className={classes.sectionHeader}>
                <Typography variant="h2">
                  {t("file_details_additional_contributors")}{" "}
                  <Typography variant="body2">
                    {t("file_details_additional_contributors_sub")}
                  </Typography>
                  {!isQatarProject && (
                    <Typography variant="body2">
                      {t(
                        "file_details_additional_contributors_pen_name",
                        "Use a pen name? You can edit the contributors below.",
                      )}
                    </Typography>
                  )}
                </Typography>
              </div>
              <div
                className={
                  hasError(errors, "contributors")
                    ? classes.contributorsContainerError
                    : classes.contributorsContainer
                }
              >
                {addIndex(map)(
                  createContributorRow(
                    onEditContributor,
                    onRemoveContributor,
                    i18n.dir() === "rtl",
                  ),
                  contributors,
                )}
              </div>
              <div>
                <Button
                  classes={{
                    label: classes.addContributorButton,
                    startIcon: clsx({
                      [classes.startIcon]: i18n.dir() === "rtl",
                    }),
                  }}
                  variant="text"
                  color="secondary"
                  onClick={onAddContributor}
                  startIcon={<AddIcon />}
                >
                  {t("file_details_add_contributor_button")}
                </Button>
                <div className={classes.contributorsError}>
                  <p>{t(firstError(errors, "contributors"))}</p>
                </div>
              </div>
            </Grid>
          )}

          {showExternalLinks && (
            <Grid item xs={12} md={6}>
              <ExternalLinks />
            </Grid>
          )}
        </Grid>

        <AddContributorDialog />
        <EditContributorDialog />
        <RemoveContributorDialog />
      </form>
      <div>
        <StepPanelButtonBar
          onNext={onNext}
          onPrevious={onPrevious}
          onCancel={onCancel}
        />
      </div>
    </Paper>
  );
};

export default FileDetails;
